import { NavLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  BadgePercent,
  Boxes,
  Image,
  Headphones,
  Bell,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Users,
  X,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import { getProfileImage } from "../core/api";
import AppImage from "./AppImage";
import { hasPermission, type AdminPermissionKey } from "../core/adminPermissions";

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
};

const navItems: Array<{
  route: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission: AdminPermissionKey;
}> = [
  { route: "/dashboard", label: "Dashboard", icon: LayoutDashboard, permission: "dashboard" },
  { route: "/admins", label: "All Admins", icon: Shield, permission: "admins" },
  { route: "/users", label: "All Users", icon: Users, permission: "users" },
  { route: "/products", label: "All Products", icon: ShoppingBag, permission: "products" },
  { route: "/categories", label: "All Categories", icon: Boxes, permission: "categories" },
  { route: "/orders", label: "All Orders", icon: ShoppingCart, permission: "orders" },
  { route: "/coupons", label: "All Coupons", icon: BadgePercent, permission: "coupons" },
  { route: "/banners", label: "All Banners", icon: Image, permission: "banners" },
  { route: "/reviews", label: "All Reviews", icon: MessageSquare, permission: "reviews" },
  { route: "/notifications", label: "Notifications", icon: Bell, permission: "notifications" },
  { route: "/support", label: "User Support", icon: Headphones, permission: "support" },
  { route: "/settings", label: "Settings", icon: Settings, permission: "settings" },
];

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { logout } = useAuth();
  const [profileData, setProfileData] = useState<any>(null);
  const profileImage = getProfileImage(profileData);
  const visibleNavItems = navItems.filter((item) => hasPermission(item.permission));

  useEffect(() => {
    const loadProfile = () => {
      const storedProfile = localStorage.getItem("adminProfile");
      try {
        setProfileData(storedProfile ? JSON.parse(storedProfile) : null);
      } catch {
        setProfileData(null);
      }
    };

    loadProfile();
    window.addEventListener("adminProfile:updated", loadProfile);
    return () => window.removeEventListener("adminProfile:updated", loadProfile);
  }, []);

  const handleLogout = async () => {
    const confirmation = await Swal.fire({
      title: "Log out?",
      text: "You will need to sign in again to access the admin panel.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Log out",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#588742",
    });

    if (!confirmation.isConfirmed) return;

    onClose();
    await logout();
  };

  const closeOnMobile = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={onClose}
          className="sidebar-backdrop"
        />
      )}

      <aside className={`app-sidebar ${isOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-brand">
          <img className="brand-logo" src={logo} alt="Sugam Store" />
          <span className="brand-monogram" aria-label="Sugam Store">
            SS
          </span>

          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.route}
                to={item.route}
                onClick={closeOnMobile}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-icon">
                  <Icon size={18} strokeWidth={2} />
                </span>
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-user">
          <AppImage
            src={profileImage}
            alt={profileData?.name || "Admin"}
            crossOrigin="anonymous"
          />

          <div>
            <strong>{profileData?.name || "Admin"}</strong>
            <small>{profileData?.role || "Admin"}</small>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            aria-label="Logout"
            title="Logout"
          >
            <LogOut size={18} strokeWidth={2} />
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
